import type {
  ExplorationView,
  JudgedCandidate,
  SourcingExplorationSummary,
} from "@shared/labs/sourcing";
import { ArrowLeft, History, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";

/** Mirrors the server bound on candidateIds per call (MAX_PICK_PER_CALL). */
const MAX_PICK = 200;

/**
 * "Trazer de uma exploração" — THE single flow for feeding a project from a
 * past run. Selection is always the recruiter's: the top-N by FIT (the
 * project's ingest setting) comes PRE-SELECTED as the default, with
 * limpar/selecionar-tudo to take manual control. Confirming also attaches the
 * run as a SESSION of the project when it's free (cost/history roll up); a
 * run already owned by another project contributes candidates only. Free of
 * Apify spend — the only cost is the cheap re-judge when briefs differ.
 */
export function PickFromRunDialog({
  open,
  onOpenChange,
  projectId,
  ingestTopN,
  initialExplorationId,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  /** The project's top-N setting — the size of the default pre-selection. */
  ingestTopN: number;
  /** When set, the dialog opens straight into this run's candidate picker
   *  (skips the run-list step) — used by the "Selecionar candidatos" CTA a
   *  just-finished session shows. The "Outras buscas" back button still works. */
  initialExplorationId?: string;
  /** Fires after a successful add with the ids of candidates already in the
   *  project (collisions) so the caller can offer to re-triage them. */
  onAdded: (skippedCandidateIds: string[]) => void;
}) {
  const { toast } = useToast();
  const [runs, setRuns] = useState<SourcingExplorationSummary[] | null>(null);
  const [run, setRun] = useState<ExplorationView | null>(null);
  const [loadingRun, setLoadingRun] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: openRun is a stable closure over setState setters; listing it would re-run the effect every render.
  useEffect(() => {
    if (!open) {
      setRuns(null);
      setRun(null);
      setPicked(new Set());
      return;
    }
    let cancelled = false;
    rpcClient.labs
      .sourcingExplorationList({ limit: 20 })
      .then((rows: SourcingExplorationSummary[]) => {
        if (!cancelled) setRuns(rows.filter((r) => r.status === "done" && r.resultCount > 0));
      })
      .catch(() => {
        if (!cancelled) setRuns([]);
      });
    // Deep-open onto a specific run (the "Selecionar candidatos" CTA) — the run
    // list still loads in the background so "Outras buscas" works.
    if (initialExplorationId) void openRun(initialExplorationId);
    return () => {
      cancelled = true;
    };
  }, [open, initialExplorationId]);

  async function openRun(id: string) {
    setLoadingRun(true);
    try {
      const v: ExplorationView = await rpcClient.labs.sourcingExplorationGet({
        explorationId: id,
      });
      setRun(v);
      // The machine proposes, the recruiter disposes: default = top-N by FIT
      // (results arrive FIT-ranked), same size as the auto-ingest setting.
      setPicked(new Set(v.results.slice(0, Math.min(ingestTopN, MAX_PICK)).map((c) => c.id)));
    } catch (err) {
      toast({
        title: "Falha ao abrir a exploração",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setLoadingRun(false);
    }
  }

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_PICK) next.add(id);
      return next;
    });
  }

  async function add() {
    if (!run || picked.size === 0 || submitting) return;
    setSubmitting(true);
    try {
      const { added, skipped, skippedCandidateIds, attached } =
        await rpcClient.labs.sourcingProjectAddFromExploration({
          projectId,
          explorationId: run.id,
          candidateIds: Array.from(picked),
        });
      const parts = [
        `${added} ${added === 1 ? "candidato trazido" : "candidatos trazidos"}`,
        ...(skipped > 0 ? [`${skipped} já no projeto`] : []),
      ];
      toast({
        title: parts.join(" · "),
        description: attached
          ? "A exploração agora é uma sessão deste projeto — custo e histórico contam aqui."
          : "A exploração pertence a outro projeto — só os candidatos foram trazidos.",
      });
      onOpenChange(false);
      onAdded(skippedCandidateIds);
    } catch (err) {
      toast({
        title: "Falha ao trazer candidatos",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const ownedElsewhere = (r: SourcingExplorationSummary) =>
    Boolean(r.projectId) && r.projectId !== projectId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> Trazer de uma exploração
          </DialogTitle>
          <DialogDescription>
            Os melhores {ingestTopN} por FIT já vêm selecionados — ajuste como quiser. A busca vira
            uma sessão do projeto (custo e histórico contam aqui), sem custo novo de busca.
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-1 flex-1 space-y-1.5 overflow-y-auto px-1">
          {run === null && runs === null && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando buscas…
            </div>
          )}

          {run === null && runs?.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">
              Nenhuma busca concluída com candidatos ainda.
            </p>
          )}

          {run === null &&
            runs?.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => void openRun(r.id)}
                disabled={loadingRun}
                className="w-full rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-ring disabled:opacity-60"
              >
                <p className="line-clamp-1 text-[13px] font-medium">{r.brief}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {r.resultCount} candidatos · {r.kind === "explore" ? "Explorar" : "Busca precisa"}{" "}
                  · {new Date(r.createdAt).toLocaleDateString("pt-BR")}
                  {r.projectId === projectId && " · sessão deste projeto"}
                  {ownedElsewhere(r) && " · em outro projeto (só candidatos)"}
                </p>
              </button>
            ))}

          {run && (
            <>
              <div className="flex items-center justify-between gap-2 pb-1">
                <Button variant="ghost" size="sm" onClick={() => setRun(null)}>
                  <ArrowLeft /> Outras buscas
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] tabular-nums text-muted-foreground">
                    {picked.size} selecionados
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setPicked(new Set())}>
                    Limpar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setPicked(new Set(run.results.slice(0, MAX_PICK).map((c) => c.id)))
                    }
                  >
                    Selecionar tudo
                  </Button>
                </div>
              </div>
              {run.results.map((c: JudgedCandidate, i: number) => (
                <label
                  key={c.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 transition-colors hover:border-ring"
                >
                  <Checkbox
                    checked={picked.has(c.id)}
                    onCheckedChange={() => toggle(c.id)}
                    disabled={picked.size >= MAX_PICK && !picked.has(c.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium">
                      {c.name || "Perfil sem nome"}
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {[c.currentTitle, c.location].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                    #{i + 1}
                  </span>
                </label>
              ))}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={add} disabled={!run || picked.size === 0 || submitting}>
            {submitting && <Loader2 className="animate-spin" />}
            Trazer {picked.size > 0 && <span className="tabular-nums">({picked.size})</span>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
